import { AppIcon } from '@/components/ui/app-icon';
import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';

import {
  buildBookingUrl,
  calendarUrlForSeats,
} from '@/components/onboarding-booking-step.component';
import { PRIMARY_BUTTON } from '@/components/ui/button';
import { UpgradeLink } from '@/components/upgrade/upgrade-link.component';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useGetMyOnboardingCallCreditQuery } from '@/store/features/onboarding-call-credits.feature';
import { iconArrowUpRight, iconXmark } from '@/lib/icons';
import { CARD_TRIAL_DAYS, TRIAL_ENDING_SOON_DAYS } from '@/lib/card-trial';
import { toUpgradeTier } from '@/lib/upgrade-link';
import { cn } from '@/lib/utils';
import { useUser } from '@clerk/react';

/** Per-user dismissal. Short TTL so a live offer resurfaces rather than being
 *  gone for good after one stray click. */
const DISMISS_KEY_PREFIX = 'cl_trial_offer_dismissed_';
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;

function readDismissed(userId: string): boolean {
  try {
    const raw = localStorage.getItem(`${DISMISS_KEY_PREFIX}${userId}`);
    return raw ? Date.now() - Number(raw) < DISMISS_TTL_MS : false;
  } catch {
    return false;
  }
}

function writeDismissed(userId: string) {
  try {
    localStorage.setItem(`${DISMISS_KEY_PREFIX}${userId}`, String(Date.now()));
  } catch {
    // Storage unavailable (private mode) - dismissal just won't persist.
  }
}

/** Whole days left, rounded UP so any time still on the clock reads as a day
 *  rather than "0 days". Deliberately mirrors the server's own
 *  `trial.days_remaining` (`(ends - now).days + 1 if seconds`), so the live
 *  count and the API fallback below can never disagree by a day.
 *  Null when there is nothing sensible left to count down to. */
function daysUntil(endsAtMs: number | null): number | null {
  if (endsAtMs === null || !Number.isFinite(endsAtMs)) return null;
  const ms = endsAtMs - Date.now();
  if (ms <= 0) return null;
  return Math.ceil(ms / 86_400_000);
}

const dayCount = (days: number) => `${days} day${days === 1 ? '' : 's'}`;

/**
 * Full-width trial bar in the AppShell banner slot, above both the nav sidebar
 * and the page on every screen (its measured height feeds --app-banner-h).
 * Two forms:
 *
 * - Card-backed trial: a status line (plan and days left) and an onboarding
 *   call link, shown for as long as the trial runs.
 * - Any other trial: the onboarding-call offer, hidden once the call is booked
 *   or the offer is dismissed.
 *
 * Neither renders for an org with no trial, or mid-onboarding, where signup has
 * its own booking step. `completed_onboarding` is presentation-only, so reading
 * it from user-writable `unsafeMetadata` is fine. "Booked" is the opposite: it
 * comes from the API (a request opened by Calendly's webhook), because
 * user-writable state must never be what authorises the 500 credits.
 *
 * Surface tokens rather than a brand-colour slab, which reads as an alert and
 * glares in dark mode; the one primary element is the CTA.
 */
export function TrialStatusBanner() {
  const posthog = usePostHog();
  const { trial, reducedCreditCountry } = useFeatureAccess();
  const { user } = useUser();
  const userId = user?.id;

  // `?previewTrial=1` renders the bar for orgs that aren't on a trial.
  // DEV-only, so it never reaches a production bundle.
  const previewTrial =
    import.meta.env.DEV &&
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('previewTrial') === '1';

  // Reduced-credit-country signups (IN/PK/PH/CN) are not offered the call.
  // Fail CLOSED (`=== false`): an unknown answer hides it. previewTrial
  // force-shows the bar in DEV.
  const bookingEnabled = previewTrial || reducedCreditCountry === false;

  const { data: callCredit, isSuccess: bookingStatusLoaded } =
    useGetMyOnboardingCallCreditQuery(undefined, { skip: previewTrial });
  const bookedOnServer =
    callCredit?.status === 'pending' || callCredit?.status === 'approved';
  // The API is the source of truth for "booked" (see file header). Until that
  // query has actually resolved we do NOT know the user is unbooked, so treat
  // an unresolved query (loading / error / undefined) as booked and withhold
  // the direct Calendly CTA — otherwise it flashes for a user who already has a
  // call on the books. `previewTrial` skips the query on purpose and must keep
  // showing the bar, so it is excluded from the unresolved guard.
  const bookingStatusUnresolved = !previewTrial && !bookingStatusLoaded;
  const hasBookedCall =
    !previewTrial &&
    (bookingStatusUnresolved ||
      bookedOnServer ||
      user?.unsafeMetadata?.onboarding_call_booked === true);
  const wentPaid = !previewTrial && trial?.plan_chosen === 'paid';

  const onTrial = previewTrial || trial?.active === true;

  // Onboarding owns the first-run experience and carries its own booking step;
  // the bar is the follow-up nudge for people who finished without booking.
  const hasOnboarded =
    previewTrial || user?.unsafeMetadata?.completed_onboarding === true;

  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (userId) setDismissed(readDismissed(userId));
  }, [userId]);

  // Card-backed trial: the bar is the trial's status line and always shows.
  const renewal = previewTrial ? null : (trial?.renews_to ?? null);
  const cardTrial = onTrial && renewal !== null;

  const visible = cardTrial
    ? hasOnboarded && !wentPaid
    : bookingEnabled &&
      onTrial &&
      hasOnboarded &&
      !hasBookedCall &&
      !wentPaid &&
      !dismissed;

  // Hooks must run unconditionally, so the tick is declared before any return.
  // A whole-day label only changes at a day boundary, so this re-renders once a
  // minute rather than once a second - enough to roll the count over promptly
  // on a session left open overnight, without 86,400 wasted renders a day.
  const [, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!visible) return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [visible]);

  if (!visible) return null;

  // Past the guard above, the org is always on a trial, so the countdown is
  // always the trial's own - derived from the date the server granted, never
  // from an org-age heuristic.
  const endsAtMs = previewTrial
    ? Date.now() + 3 * 86_400_000 + 3_600_000
    : trial?.ends_at
      ? Date.parse(trial.ends_at)
      : null;

  // A trial the server still calls active, whose clock has run out or gone
  // missing, falls back to the server's own day count - client skew or a stale
  // timestamp must not quietly drop the deadline.
  const daysLeft =
    daysUntil(endsAtMs) ?? Math.max(0, trial?.days_remaining ?? 0);

  const seats =
    typeof user?.unsafeMetadata?.seats === 'string'
      ? user.unsafeMetadata.seats
      : undefined;
  const referral =
    typeof user?.unsafeMetadata?.referral_source === 'string'
      ? user.unsafeMetadata.referral_source
      : undefined;
  const bookingHref = buildBookingUrl(
    calendarUrlForSeats(seats, user?.primaryEmailAddress?.emailAddress),
    {
      name: user?.fullName ?? undefined,
      email: user?.primaryEmailAddress?.emailAddress ?? undefined,
      seats,
      referralSource: referral,
      surface: 'Trial banner',
    }
  );
  const trackBookClick = () =>
    posthog?.capture('trial_banner_book_clicked', {
      days_remaining: trial?.days_remaining ?? null,
      seats: seats ?? null,
      on_trial: onTrial,
    });
  const dismissOffer = () => {
    if (userId) writeDismissed(userId);
    setDismissed(true);
    posthog?.capture('trial_banner_dismissed', { on_trial: onTrial });
  };

  if (cardTrial && renewal) {
    // A fuel gauge: full on day one, draining as the trial runs down.
    const remaining = Math.min(1, Math.max(0, daysLeft / CARD_TRIAL_DAYS));
    const endingSoon = daysLeft <= TRIAL_ENDING_SOON_DAYS;
    return (
      <div
        role='region'
        aria-label='Free trial status'
        className={cn(
          'relative flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 border-b px-4 py-2 text-[13px] sm:flex-nowrap',
          endingSoon
            ? 'border-amber-500/30 bg-amber-50 dark:bg-amber-500/10'
            : 'border-border bg-card bg-[linear-gradient(90deg,hsl(var(--primary)/0.05)_0%,transparent_45%,hsl(var(--primary)/0.05)_100%)]'
        )}
      >
        <span className='flex shrink-0 items-center gap-2.5'>
          <span className='font-semibold text-foreground'>
            {trial?.plan === 'pro' ? 'Pro' : 'Enterprise'} trial
          </span>
          <span
            aria-hidden
            className='hidden h-1.5 w-16 overflow-hidden rounded-full bg-muted sm:block'
          >
            <span
              className={cn(
                'block h-full rounded-full',
                endingSoon ? 'bg-amber-500' : 'bg-primary'
              )}
              style={{ width: `${Math.round(remaining * 100)}%` }}
            />
          </span>
          <span
            className={cn(
              'tabular-nums',
              endingSoon
                ? 'font-semibold text-amber-700 dark:text-amber-400'
                : 'text-muted-foreground'
            )}
          >
            {dayCount(daysLeft)} left
          </span>
        </span>

        {/* Deliberately not hidden after booking: a way in, not a nudge. */}
        {bookingEnabled && (
          <a
            href={bookingHref}
            target='_blank'
            rel='noopener noreferrer'
            onClick={trackBookClick}
            className={cn(
              PRIMARY_BUTTON,
              'inline-flex h-7 shrink-0 items-center gap-1.5 px-3 whitespace-nowrap',
              'text-xs font-semibold',
              'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-hidden'
            )}
          >
            Book onboarding call
            <AppIcon icon={iconArrowUpRight} style={{ fontSize: 10 }} />
          </a>
        )}
      </div>
    );
  }

  return (
    <div
      role='region'
      aria-label='Free trial status'
      className={cn(
        'relative flex w-full flex-wrap items-center justify-center gap-x-4 gap-y-2',
        'border-b border-border bg-card px-4 py-2 pr-11 text-[13px] sm:flex-nowrap',
        // A whisper of brand along the edges so the strip reads as ours without
        // becoming a coloured slab. Slightly stronger in dark, where a 5% tint
        // on a near-black surface would disappear entirely.
        'bg-[linear-gradient(90deg,hsl(var(--primary)/0.05)_0%,transparent_45%,hsl(var(--primary)/0.05)_100%)]',
        'dark:bg-[linear-gradient(90deg,hsl(var(--primary)/0.11)_0%,transparent_45%,hsl(var(--primary)/0.11)_100%)]'
      )}
    >
      {/* Always present: the bar only renders during a trial, so there is
          always a count to show - the live one, or the server's own. */}
      <span className='shrink-0 text-muted-foreground'>
        <span className='font-semibold text-foreground tabular-nums'>
          {dayCount(daysLeft)}
        </span>
        <span className='hidden sm:inline'> remaining in your trial</span>
      </span>

      <span
        aria-hidden
        className='hidden h-4 w-px shrink-0 bg-border sm:block'
      />

      <span className='min-w-0 truncate text-foreground'>
        Book your onboarding call
      </span>

      <a
        href={bookingHref}
        target='_blank'
        rel='noopener noreferrer'
        onClick={trackBookClick}
        className={cn(
          PRIMARY_BUTTON,
          'inline-flex h-7 shrink-0 items-center gap-1.5 px-3 whitespace-nowrap',
          'text-xs font-semibold',
          'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-hidden'
        )}
      >
        Book now
        <AppIcon icon={iconArrowUpRight} style={{ fontSize: 10 }} />
      </a>

      <UpgradeLink
        source='trial_banner'
        plan={toUpgradeTier(trial?.plan)}
        onClick={() =>
          posthog?.capture('trial_banner_upgrade_clicked', {
            days_remaining: trial?.days_remaining ?? null,
          })
        }
        className='hidden shrink-0 whitespace-nowrap text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline sm:inline'
      >
        Upgrade
      </UpgradeLink>

      <button
        type='button'
        aria-label='Dismiss'
        onClick={dismissOffer}
        className='absolute top-1/2 right-3 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden'
      >
        <AppIcon icon={iconXmark} style={{ fontSize: 12 }} />
      </button>
    </div>
  );
}
